import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Layers, RotateCcw, Info } from "lucide-react";

const PRESETS = [
  { s: 0, l: "Nessuna" },
  { s: 5, l: "5 secondi" },
  { s: 10, l: "10 s (consigliato)" },
  { s: 30, l: "30 secondi" },
  { s: 60, l: "1 minuto" },
  { s: 300, l: "5 minuti" },
];

export default function DedupSection() {
  const [seconds, setSeconds] = useState(10);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const load = () =>
    api.get("/settings/dedup-window").then(({ data }) => setSeconds(data.seconds));

  useEffect(() => {
    load();
  }, []);

  const save = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.put("/settings/dedup-window", { seconds: s });
      setSeconds(data.seconds);
      toast.success(
        data.seconds === 0
          ? "Deduplicazione disattivata"
          : `Finestra impostata a ${data.seconds} secondi`
      );
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const reprocess = async () => {
    if (
      !window.confirm(
        "Verranno rielaborati gli eventi già registrati collassando quelli entro la finestra scelta. I contatori campagne verranno ricalcolati. Procedere?"
      )
    )
      return;
    setReprocessing(true);
    try {
      const { data } = await api.post("/settings/dedup-existing");
      toast.success(
        `${data.deleted} eventi duplicati rimossi · ${data.recomputed_campaigns} campagne aggiornate`
      );
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div className="border border-border bg-white p-6" data-testid="dedup-section">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-5 h-5 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-xl">Deduplicazione eventi ravvicinati</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Quando un mail scanner (Fastly, Barracuda, Mimecast…) pre-carica il pixel prima
        dell'utente reale, vengono generate 2+ aperture a pochi secondi di distanza. Con
        questa finestra vengono considerate come un unico accesso.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">
            Finestra di deduplicazione
          </Label>
          <div className="flex flex-wrap gap-2 mt-3">
            {PRESETS.map((p) => (
              <Button
                key={p.s}
                variant={seconds === p.s ? "default" : "outline"}
                size="sm"
                disabled={loading}
                onClick={() => save(p.s)}
                data-testid={`dedup-preset-${p.s}`}
              >
                {p.l}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Input
              type="number"
              min={0}
              max={3600}
              value={seconds}
              onChange={(e) => setSeconds(Number(e.target.value) || 0)}
              className="w-28"
              data-testid="dedup-seconds-input"
            />
            <span className="text-sm text-muted-foreground">secondi</span>
            <Button
              size="sm"
              onClick={() => save(seconds)}
              disabled={loading}
              data-testid="dedup-save"
            >
              Salva
            </Button>
          </div>
        </div>

        <div className="border border-border bg-muted/30 p-4 rounded-sm">
          <div className="flex items-start gap-2 mb-2">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5" strokeWidth={1.5} />
            <div className="text-sm">
              <div className="font-medium">Come funziona</div>
              <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                Se lo stesso contatto genera 2 aperture (o 2 click) entro{" "}
                <strong>{seconds} secondi</strong>, la seconda viene ignorata: né
                registrata negli eventi, né conteggiata nelle statistiche. Impostando a{" "}
                <strong>0</strong> disattivi la deduplicazione.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={reprocess}
          disabled={reprocessing || seconds === 0}
          data-testid="dedup-reprocess-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {reprocessing
            ? "Rielaborazione in corso…"
            : "Rielabora eventi già registrati"}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          Utile solo la prima volta: applica la deduplicazione anche a tutti gli eventi già
          salvati nelle campagne passate e ricalcola i contatori.
        </p>
      </div>
    </div>
  );
}
